import {
  BookingParams,
  BookingPosition,
  BookingResult,
  BookingTransactionId,
  SystemContext,
  TransactionQueuedBooking,
} from '../types';
import { ActionTranslator } from '../actions/action-translator';
import { ModelAdapters } from '../interface/model-adapters';
import { BookingClientIds } from '../booking-client-ids';

/**
 * Führt die Transaktions-Operationen aus: Commit und Discard leeren die Queue,
 * Rollback bucht die enthaltenen Buchungen einzeln gegen.
 */
export class TransactionHandler {
  /**
   * @param translator Übersetzt eine Position/Params in die passende Buchungs-Action (für die Gegenbuchungen des Rollbacks)
   */
  constructor(private readonly translator: ActionTranslator) {}

  /**
   * Schließt die Transaktion ab, indem die Queue der Transaktion geleert wird.
   *
   * @param id ID der Transaktion
   * @param adapters Model-Adapter (Zugriff auf den Transaction-Store)
   * @returns Erfolg, oder Fehler mit ID-Hinweis, wenn das Leeren fehlschlägt
   */
  public async commit(
    id: BookingTransactionId,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    const deleted = adapters.transaction.clear(id);
    if (deleted) {
      return { success: true, message: '' };
    }
    return {
      success: false,
      message: `Unable to clear transaction id ${id} from storage`,
    };
  }

  /**
   * Rollt die vom Filter ausgewählten Buchungen der Transaktion zurück, indem
   * für jede eine invertierte Gegenbuchung ausgeführt und der Eintrag danach
   * aus der Queue entfernt wird. Bricht beim ersten Fehler ab (fail-fast).
   *
   * @param id ID der Transaktion
   * @param adapters Model-Adapter (Zugriff auf den Transaction-Store)
   * @param systemContext Aktueller System-Kontext für die Gegenbuchungen
   * @param filter Prädikat, das die zurückzurollenden Einträge auswählt
   * @returns Erfolg, sobald alle Gegenbuchungen durchliefen, sonst das Fehlerergebnis der ersten fehlgeschlagenen Gegenbuchung
   */
  public async rollback(
    id: BookingTransactionId,
    adapters: ModelAdapters,
    systemContext: SystemContext,
    filter: (qb: TransactionQueuedBooking) => boolean,
  ): Promise<BookingResult> {
    const queuedBookings = adapters.transaction.findBookings(id, filter);
    for (const qb of queuedBookings) {
      const { position, params } = this.buildCounterBooking(
        qb,
        adapters,
        systemContext,
      );
      const result = await this.translator.translateToAction(position, params);
      if (!result.success) {
        return result;
      }
      adapters.transaction.removeBookings(
        id,
        x => x.entryId === qb.entryId
      );
    }
    return { success: true, message: '' };
  }

  /**
   * Verwirft die Transaktion, indem die Queue der Transaktion geleert wird,
   * ohne gegenzubuchen.
   *
   * @param id ID der Transaktion
   * @param adapters Model-Adapter (Zugriff auf den Transaction-Store)
   * @returns Erfolg, oder Fehler mit ID-Hinweis, wenn das Leeren fehlschlägt
   */
  public async discard(
    id: BookingTransactionId,
    adapters: ModelAdapters,
  ): Promise<BookingResult> {
    const deleted = adapters.transaction.clear(id);
    if (deleted) {
      return { success: true, message: '' };
    }
    return {
      success: false,
      message: `Unable to clear transaction id ${id} from storage`,
    };
  }

  /**
   * Baut Position und Params der Gegenbuchung zu einer queued Buchung: die
   * Menge wird invertiert, die transactionId gestrippt (verhindert erneutes
   * Einreihen) und die entryId als deterministische client_id gesetzt (macht
   * einen wiederholten Rollback idempotent).
   *
   * @param qb Zurückzurollende queued Buchung
   * @param adapters Model-Adapter, die an die Gegenbuchung durchgereicht werden
   * @param system Aktueller System-Kontext für die Gegenbuchung
   * @returns Position und Params für den Gegenbuchungs-Aufruf
   */
  private buildCounterBooking(
    qb: TransactionQueuedBooking,
    adapters: ModelAdapters,
    system: SystemContext,
  ): { position: BookingPosition; params: BookingParams } {
    const position = { ...qb.position, amount: -qb.position.amount };
    return {
      position,
      params: {
        context: {
          // Unset transactionId, so inverted booking is not enqueued
          booking: { ...qb.booking, transactionId: undefined },
          system,
        },
        adapters,
        // entryId als client_id des Counter-Movements: ein wiederholter
        // Rollback desselben Eintrags sendet dieselbe ID und kompensiert
        // nie doppelt.
        clientIds: BookingClientIds.fixedFor(position, qb.entryId),
      },
    };
  }
}
